/**
 * Op-коды протокола и разбор тел сообщений.
 *
 * Значения обязаны совпадать с contracts/types.tolk. Индексер читает те же
 * сообщения, что обрабатывают контракты, поэтому расхождение здесь означает
 * молча пропущенные события — витрина отстанет от блокчейна, и никто этого
 * не заметит, пока пользователь не пожалуется.
 */

import { Address, type Cell, type Slice } from '@ton/core';

// Стандарты
export const OP_OWNERSHIP_ASSIGNED = 0x05138d91;
export const OP_NFT_TRANSFER = 0x5fcc3d14;

// Сделка
export const OP_PAY = 0x50415900;
export const OP_DEFERRAL = 0x44454600;
export const OP_BUY_POSITION = 0x42555900;
export const OP_CLAIM_DEFAULT = 0x44454641;
export const OP_ASSIGN_POSITION = 0x41535347;
export const OP_LIST_POSITION = 0x4c495354;
export const OP_CANCEL_DEAL = 0x43414e43;
export const OP_WITHDRAW_OWED = 0x57444857;

// Мастер
export const OP_CREATE_DEAL = 0x43524554;
export const OP_SET_FEE = 0x53455446;
export const OP_SET_COLLECTION = 0x53455443;
export const OP_SET_DEAL_CODE = 0x53455444;
export const OP_SET_REF_PUBKEY = 0x53455250;
export const OP_SET_NEXT_ADMIN = 0x534e4144;
export const OP_ACCEPT_ADMIN = 0x41434144;
export const OP_WITHDRAW_FEES = 0x57465045;

/**
 * Надбавка на обработку платежа — ровно та, что вычитает контракт.
 * Расхождение здесь означает, что индексер считает не то, что сделка.
 */
export const PAYMENT_GAS = 20_000_000n;

/** Длина подписи Ed25519 в битах — ровно столько читает контракт. */
export const SIGNATURE_BITS = 512;

/**
 * Раскладка начальных данных NFT-айтема — то, из чего мастер считает адрес
 * экземпляра и чем проверяет его подлинность (§9.1).
 *
 * Эталонная TEP-62 кладёт (index: uint64, collection). Настоящие коллекции
 * кладут не это: у TON DNS индекс 256-битный, у гифтов Fragment вся статика
 * уезжает ссылкой. Значения обязаны совпадать с contracts/types.tolk.
 */
export const ITEM_LAYOUT_INDEX64 = 0;
export const ITEM_LAYOUT_INDEX256 = 1;
export const ITEM_LAYOUT_REF256 = 2;
export type ItemLayout = 0 | 1 | 2;


export type DealEventKind =
  | 'nft_locked'
  | 'payment'
  | 'deferral'
  | 'default_claimed'
  | 'position_assigned'
  | 'position_listed'
  | 'position_sold'
  | 'cancelled'
  | 'owed_withdrawn';

export interface DealEvent {
  kind: DealEventKind;
  /** Сумма поступления в нанотонах; для неденежных событий 0. */
  amount: bigint;
  /** Кто инициировал: плательщик, прежний владелец NFT, новый владелец позиции. */
  actor: Address | null;
  /**
   * Приглашающий из payload предоплаты — как его прочитал бы контракт, но
   * без проверки подписи: ключа у индексера нет, а знать, что приглашение
   * заявлено, полезно и так. Принял ли его контракт, видно по get-методу
   * самой сделки; расхождение здесь означает поддельное приглашение.
   */
  claimedReferrer?: Address | null;
}

/**
 * Приглашающий из ячейки, приложенной к предоплате — повторяет parseReferrer
 * из контракта во всём, кроме проверки подписи: приватного ключа у индексера
 * нет, а публичный лежит в сделке. Заявленное приглашение и принятое — разные
 * вещи, и различать их полезно: расхождение это попытка подделки.
 */
export function parseClaimedReferrer(payload: Cell | null): Address | null {
  if (payload === null) return null;
  const s = payload.beginParse();
  if (s.remainingBits < 267 + SIGNATURE_BITS) return null;
  return s.loadAddress();
}

/**
 * Разбор входящего сообщения сделки. Возвращает null для всего, что протокол
 * не считает событием, — например для возвратов излишков газа.
 */
/**
 * Разбор входящего сообщения сделки. Возвращает null для всего, что протокол
 * не считает событием, — например для пополнений газа пустым телом.
 *
 * `value` — сумма самого сообщения: деньги теперь едут ею, а не полем в теле.
 * В счёт долга идёт ровно столько же, сколько зачтёт контракт.
 */
export function parseDealMessage(body: Cell, value = 0n): DealEvent | null {
  const s = body.beginParse();
  if (s.remainingBits < 32) return null;
  const op = s.loadUint(32);
  const credited = value > PAYMENT_GAS ? value - PAYMENT_GAS : 0n;

  switch (op) {
    case OP_OWNERSHIP_ASSIGNED: {
      s.loadUintBig(64); // queryId
      const prevOwner = s.loadAddress();
      return { kind: 'nft_locked', amount: 0n, actor: prevOwner };
    }
    case OP_PAY: {
      s.loadUintBig(64);
      return {
        kind: 'payment',
        amount: credited,
        actor: null,
        claimedReferrer: parseClaimedReferrer(s.loadMaybeRef()),
      };
    }
    case OP_DEFERRAL:
      return { kind: 'deferral', amount: credited, actor: null };
    case OP_BUY_POSITION:
      // Выкуп позиции — те же деньги тем же путём, но не в счёт рассрочки:
      // они уходят прежнему владельцу позиции.
      return { kind: 'position_sold', amount: credited, actor: null };
    case OP_CLAIM_DEFAULT:
      return { kind: 'default_claimed', amount: 0n, actor: null };
    case OP_ASSIGN_POSITION: {
      s.loadUintBig(64);
      return { kind: 'position_assigned', amount: 0n, actor: s.loadAddress() };
    }
    case OP_LIST_POSITION: {
      s.loadUintBig(64);
      // Сумма здесь — назначенная цена, а не поступление: событие денег не
      // приносит. Ноль означает «снято с продажи».
      return { kind: 'position_listed', amount: s.loadCoins(), actor: null };
    }
    case OP_CANCEL_DEAL:
      return { kind: 'cancelled', amount: 0n, actor: null };
    case OP_WITHDRAW_OWED:
      return { kind: 'owed_withdrawn', amount: 0n, actor: null };
    default:
      return null;
  }
}

export interface CreateDealMessage {
  collection: Address;
  nftAddress: Address;
  nftIndex: bigint;
  nonce: bigint;
}

/**
 * Разбор create_deal, отправленного мастеру. Условия лежат в отдельной
 * ячейке — здесь берётся только то, что нужно, чтобы связать сделку с её
 * NFT; полные условия индексер читает get-методами самой сделки.
 */
export function parseCreateDeal(body: Cell): CreateDealMessage | null {
  const s = body.beginParse();
  if (s.remainingBits < 32) return null;
  if (s.loadUint(32) !== OP_CREATE_DEAL) return null;

  s.loadUintBig(64); // queryId
  const collection = s.loadAddress();
  const nftAddress = s.loadAddress();
  const nftIndex = s.loadUintBig(256);
  const nonce = s.loadUintBig(64);
  return { collection, nftAddress, nftIndex, nonce };
}

/**
 * Админская операция мастера — то, что видно снаружи по телу сообщения.
 *
 * Читается индексером, чтобы вести список допущенных коллекций и валют:
 * контракт хранит их в словаре, а словарь get-методом не перечислить —
 * есть только проверка «эта в списке?».
 */
export type AdminOp =
  | { kind: 'fee'; feeBps: number }
  | { kind: 'collection'; collection: Address; added: boolean; layout: ItemLayout }
  | { kind: 'refPubkey'; pubkey: bigint }
  | { kind: 'nextAdmin'; nextAdmin: Address | null }
  | { kind: 'adminAccepted' }
  | { kind: 'dealCode' };

export function parseAdminOp(body: Cell): AdminOp | null {
  const s = body.beginParse();
  if (s.remainingBits < 32) return null;
  const op = s.loadUint(32);
  if (
    op !== OP_SET_FEE &&
    op !== OP_SET_COLLECTION &&
    op !== OP_SET_REF_PUBKEY &&
    op !== OP_SET_NEXT_ADMIN &&
    op !== OP_ACCEPT_ADMIN &&
    op !== OP_SET_DEAL_CODE
  ) {
    return null;
  }
  s.loadUintBig(64); // queryId

  switch (op) {
    case OP_SET_FEE:
      return { kind: 'fee', feeBps: s.loadUint(16) };
    case OP_SET_COLLECTION: {
      // Ссылка отсутствует — коллекцию убрали из реестра.
      const collection = s.loadAddress();
      const added = s.loadMaybeRef() !== null;
      const layout = (s.remainingBits >= 8 ? s.loadUint(8) : 0) as ItemLayout;
      return { kind: 'collection', collection, added, layout };
    }
    case OP_SET_REF_PUBKEY:
      return { kind: 'refPubkey', pubkey: s.loadUintBig(256) };
    case OP_SET_NEXT_ADMIN:
      return { kind: 'nextAdmin', nextAdmin: s.loadMaybeAddress() };
    case OP_ACCEPT_ADMIN:
      return { kind: 'adminAccepted' };
    default:
      return { kind: 'dealCode' };
  }
}
